import { useNavigate } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { PayrollSetupList } from './PayrollSetupList'
import {
  FETCH_COMPANY_FEDERAL_TAXES_KEY,
  fetchCompanyFederalTaxes,
  fetchCompanyIndustry,
  PUT_UPDATE_COMPANY_FEDERAL_TAXES_KEY,
  PUT_UPDATE_COMPANY_INDUSTRY_KEY,
  putUpdateCompanyFederalTaxes,
  putUpdateCompanyIndustry,
} from '../payrollActions'
import {
  GEP_ENROLL_PATHS,
  INDUSTRY_OPTIONS,
  mapTaxType,
  ONBOARDING_STEPS,
  useUpdatePayrollOnboardingStep,
} from '../helpers'
import { getCurrentUser } from '@/selectors/user.selectors'
import { PrefilledNoticeContainer } from './PrefilledNoticeContainer'
import { PayrollError } from '../PayrollError'
import {
  Button,
  Card,
  Form,
  Link,
  Popover,
  Text,
  Icon,
  Label,
  Separator,
  Alert,
} from '@/ui'
import {
  getIsFetchingOrNotStarted,
  selectIsFetchingForKeys,
} from '@/reducers/fetch'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { updateFinancialProfile } from '@/actions/financialProfileActions'
import {
  Gusto_CompanyFederalTaxes,
  Gusto_EntityType,
} from '../generated_gusto_types'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'

// TODO: Migrate to V2
import {
  makeReqStringSchema,
  getFieldName,
  makeSsnSchema,
  einRegex,
} from '@/base'
import { TypographyStyles } from '@/styles'

const TAX_PAYER_TYPES: Gusto_EntityType[] = [
  'C-Corporation',
  'S-Corporation',
  'Sole proprietor',
  'Limited partnership',
  'General partnership',
  'LLC',
]
const taxPayerTypeOptions = TAX_PAYER_TYPES.map((val) => {
  if (val === 'S-Corporation') {
    return { text: 'S corporation', value: val }
  }
  return { text: val, value: val }
})

const validationSchema = yup.object({
  industry: makeReqStringSchema({ field: 'industry' }),
  taxPayerType: makeReqStringSchema({ field: 'tax payer type' }),
  ein: yup
    .string()
    .when('taxPayerType', ([taxPayerType], schema) =>
      taxPayerType !== 'Sole proprietor'
        ? yup
            .string()
            .nullable()
            .length(10, 'Must be exactly 9 digits')
            .matches(einRegex, 'Please enter a valid value')
            .required()
        : schema
    ),
  ssn: yup
    .string()
    .when('taxPayerType', ([taxPayerType], schema) =>
      taxPayerType === 'Sole proprietor' ? makeSsnSchema({}) : schema
    ),
})

const getDefaultEin = (einNum?: string | null) => {
  if (!einNum) {
    return ''
  }

  return /^\d{9}$/.test(einNum)
    ? `${einNum.slice(0, 2)}-${einNum.slice(2)}`
    : einNum
}

const FederalForm = ({
  industry,
  federalTaxes,
}: {
  industry?: string | null
  federalTaxes?: Gusto_CompanyFederalTaxes
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()

  const user = useReselector(getCurrentUser)
  const isPosting = useReselector(selectIsFetchingForKeys, [
    PUT_UPDATE_COMPANY_INDUSTRY_KEY,
    PUT_UPDATE_COMPANY_FEDERAL_TAXES_KEY,
  ])

  const formik = useFormik({
    initialValues: {
      industry: industry || '',
      taxPayerType:
        federalTaxes?.tax_payer_type ||
        mapTaxType(user?.financialProfile?.taxEntityType),
      filingForm: federalTaxes?.filing_form || ('' as const),
      ein: getDefaultEin(user?.financialProfile?.einNumber),
      ssn: '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async ({ filingForm, taxPayerType, industry, ein, ssn }) => {
      if (!filingForm || !taxPayerType || !federalTaxes?.version) {
        return
      }

      const hasEin = taxPayerType !== 'Sole proprietor'

      // Update the financial profile with the ein
      if (hasEin && user?.financialProfile) {
        const fpRes = await updateFinancialProfile(user.financialProfile.id, {
          einNumber: ein,
        })(dispatch)

        if (!fpRes) {
          return
        }
      }

      const industryRes = await putUpdateCompanyIndustry(
        INDUSTRY_OPTIONS[industry]
      )(dispatch)

      if (!industryRes) {
        return
      }

      const federalTaxesRes = await putUpdateCompanyFederalTaxes({
        version: federalTaxes.version,
        taxable_as_scorp: taxPayerType === 'S-Corporation',
        tax_payer_type: taxPayerType,
        filing_form: filingForm,
        ein: hasEin ? ein : ssn,
      })(dispatch)

      if (federalTaxesRes) {
        track('completed payroll step - set company federal taxes and industry')
        navigate(GEP_ENROLL_PATHS.locations)
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col gap-6">
        <Text variant={TypographyStyles.H2} className="mt-4">
          Add Tax Information
        </Text>
        <Text variant={TypographyStyles.BODY_MD}>
          Please add and confirm the following details.
        </Text>

        <div className="flex flex-column gap-x-4 gap-y-6">
          <div>
            <Label>
              Tax Entity{' '}
              <Link
                href="https://support.joinheard.com/hc/en-us/articles/4761703149463"
                newPage
              >
                Help me with this
              </Link>
            </Label>
            <Form.Select
              name={getFieldName<typeof formik.values>('taxPayerType')}
              options={taxPayerTypeOptions}
              placeholder="Select Tax Entity"
              required
            />
          </div>
          <div>
            <Popover>
              <Popover.Trigger asChild>
                <Icon className="tooltip" icon={faInfoCircle} />
              </Popover.Trigger>
              <Popover.Content>
                <Text>
                  If your business is organized as a PLLC, please select LLC as
                  they are treated the same way.
                </Text>
              </Popover.Content>
            </Popover>
          </div>

          <div className="w-full lg:w-3/4">
            {formik.values.taxPayerType === 'Sole proprietor' ? (
              <Form.Input
                name={getFieldName<typeof formik.values>('ssn')}
                label="SSN"
                required
                format="###-##-####"
                componentType="pattern"
              />
            ) : (
              <Form.Input
                name={getFieldName<typeof formik.values>('ein')}
                required
                label="EIN"
                format="##-#######"
                componentType="pattern"
              />
            )}
          </div>

          <div className="w-full lg:w-3/4">
            <Label>
              Federal Filing Form{' '}
              <Link
                href="https://support.joinheard.com/hc/en-us/articles/7479704593687"
                newPage
              >
                Help me with this
              </Link>
            </Label>
            <Form.Select
              name={getFieldName<typeof formik.values>('filingForm')}
              options={[
                { text: '941', value: '941' },
                { text: '944', value: '944' },
              ]}
              description="Choose the filing form assigned to you by the IRS"
              required
            />
          </div>

          <div className="w-full">
            <Form.Select
              name={getFieldName<typeof formik.values>('industry')}
              options={Object.keys(INDUSTRY_OPTIONS).map((key) => ({
                text: key,
                value: key,
              }))}
              description="When setting up your business for payroll, the federal government requires us to provide more context on your profession. We have pre-filled this field with our best recommendation."
              label="Industry Information"
              required
            />
          </div>
        </div>

        <div className="flex gap-4 pt-2">
          <Button
            variant="secondary"
            onClick={() => navigate(GEP_ENROLL_PATHS.company)}
          >
            Back
          </Button>
          <div className="flex-1" />
          <Button onClick={formik.submitForm} loading={isPosting}>
            Save & Continue
          </Button>
        </div>
      </div>
    </FormikProvider>
  )
}

export const PayrollEnrollFederal = () => {
  const isFetchingTaxes = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_COMPANY_FEDERAL_TAXES_KEY
  )
  const industry = useFetchResponse(fetchCompanyIndustry)?.title
  const federalTaxes = useFetchResponse(fetchCompanyFederalTaxes)
  useUpdatePayrollOnboardingStep(ONBOARDING_STEPS.companyFederalTaxes)

  return (
    <Card>
      <Text variant={TypographyStyles.DISPLAY_XS}>Setting Up Payroll</Text>
      <Separator />

      <PrefilledNoticeContainer />

      <PayrollError fetchKey={PUT_UPDATE_COMPANY_INDUSTRY_KEY} />
      <PayrollError fetchKey={PUT_UPDATE_COMPANY_FEDERAL_TAXES_KEY} />

      {!isFetchingTaxes && !federalTaxes && (
        <Alert variant="error" className="mt-4">
          Something went wrong
        </Alert>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-2/3">
          <FederalForm federalTaxes={federalTaxes} industry={industry} />
        </div>
        <div className="w-full lg:w-1/3">
          <PayrollSetupList />
        </div>
      </div>
    </Card>
  )
}
